import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils/format-sub";
import { getDivisor } from "../../../../utils/stripe-helpers";

/**
 * Day/week pass sales — separate from SubscribersTable because passes are
 * one-time PaymentIntents, not Stripe subscriptions, so they never show up
 * there. Day passes have no MSB account (no login, no avatar); week passes
 * do, but both are sourced the same way here, straight from pass-purchase.
 */
const PassRows = ({ passes, currency, emptyText, t }) => (
  // biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure
  <section role="table-wrapper" data-borders-inside>
    {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
    <table role="report-table" className="passes-table">
      {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
      <thead role="table-header">
        <tr>
          <th>{t("membershipList.passesTable.buyer")}</th>
          <th>{t("membershipList.passesTable.passType")}</th>
          <th>{t("membershipList.passesTable.purchased")}</th>
          <th>{t("membershipList.passesTable.access")}</th>
          <th>{t("membershipList.passesTable.amount")}</th>
        </tr>
      </thead>
      {passes.length > 0 ? (
        <tbody>
          {passes.map((pass) => (
            <tr key={pass.paymentIntentId}>
              <td>
                <div className="flex flex-col">
                  <span className="subscriber-text">{pass.name}</span>
                  <span className="email">{pass.email}</span>
                </div>
              </td>
              <td>
                {pass.passType === "week"
                  ? t("membershipList.passesTable.weekPass")
                  : t("membershipList.passesTable.dayPass")}
              </td>
              <td>{moment(pass.purchasedAt).format("MMM D, YYYY")}</td>
              <td>
                {pass.accessDate
                  ? moment(pass.accessDate).format("MMM D, YYYY")
                  : "—"}
                {pass.accessEnd &&
                  ` – ${moment(pass.accessEnd).format("MMM D, YYYY")}`}
              </td>
              <td>
                {formatNumber(
                  (pass.amount || 0) / getDivisor(pass.currency || currency),
                  pass.currency || currency
                )}
              </td>
            </tr>
          ))}
        </tbody>
      ) : (
        <tbody>
          <tr>
            <td colSpan="100%">
              <div className="body-table-empty">
                <p>{emptyText}</p>
              </div>
            </td>
          </tr>
        </tbody>
      )}
    </table>
  </section>
);

// Active on top, expired below (sc-46010) — "expired" is the same
// accessEnd test msb-api's subscribers-list uses to drop a pass from the
// Members roster, so a pass leaves the roster and lands here together.
const PassesTable = ({ passes = [], currency = "usd" }) => {
  const { t } = useTranslation("coaching-portal");
  const now = new Date();
  const isPast = (pass) => pass.accessEnd && new Date(pass.accessEnd) <= now;
  const active = passes.filter((pass) => !isPast(pass));
  const past = passes.filter(isPast);

  return (
    <>
      <h3 className="mb-4">{t("membershipList.passesTable.activeTitle")}</h3>
      <PassRows
        passes={active}
        currency={currency}
        emptyText={t(
          passes.length > 0
            ? "membershipList.passesTable.noActive"
            : "membershipList.passesTable.empty"
        )}
        t={t}
      />
      {past.length > 0 && (
        <>
          <h3 className="mt-8 mb-4">
            {t("membershipList.passesTable.pastTitle")}
          </h3>
          <PassRows passes={past} currency={currency} t={t} />
        </>
      )}
    </>
  );
};

export default PassesTable;
